import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { markVocabularyReviewed, resolvePendingVocabulary, setVocabularySaved } from '../data/db'
import { lookupGeneralDictionary } from '../data/vocabularyLookup'
import { useVocabularyData } from '../hooks/useLocalData'
import type { VocabularyEntry, VocabularyGroup } from '../types/daily'

const difficultyOrder: VocabularyEntry['difficulty'][] = ['B2', 'C1', 'C2', 'technical', 'unrated']
const difficultyLabel = (value: VocabularyEntry['difficulty']) => value === 'technical' ? '技术词汇' : value === 'unrated' ? '未标级' : value
const formatDate = (value?: string) => value ? new Intl.DateTimeFormat('zh-CN', {
  year: 'numeric', month: 'short', day: 'numeric', timeZone: 'Asia/Singapore',
}).format(new Date(value)) : '尚未复习'

const reviewSort = (left: VocabularyGroup, right: VocabularyGroup) => {
  if (!left.lastReviewedAt && right.lastReviewedAt) return -1
  if (left.lastReviewedAt && !right.lastReviewedAt) return 1
  if (!left.lastReviewedAt && !right.lastReviewedAt) return left.firstOpenedAt.localeCompare(right.firstOpenedAt)
  return (left.lastReviewedAt ?? '').localeCompare(right.lastReviewedAt ?? '')
}

export function VocabularyPage() {
  const { data: groups, loading, error, refresh } = useVocabularyData()
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [savedOnly, setSavedOnly] = useState(false)
  const [difficulty, setDifficulty] = useState<'all' | VocabularyEntry['difficulty']>('all')
  const [reviewTerms, setReviewTerms] = useState<string[]>([])
  const [reviewIndex, setReviewIndex] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [completedThisSession, setCompletedThisSession] = useState<Set<string>>(new Set())
  const [retryingTerm, setRetryingTerm] = useState<string>()
  const [toast, setToast] = useState<string>()
  const weeklyReviewStarted = useRef(false)

  useEffect(() => {
    if (!toast) return
    const timeout = window.setTimeout(() => setToast(undefined), 2400)
    return () => window.clearTimeout(timeout)
  }, [toast])

  const selectedTerm = searchParams.get('term')
  const weeklyStart = searchParams.get('start')
  const weeklyEnd = searchParams.get('end')
  const weeklyReview = searchParams.get('review') === 'weekly' && Boolean(weeklyStart && weeklyEnd)
  const filtered = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase()
    return groups.filter((group) => {
      if (savedOnly && !group.saved) return false
      if (difficulty !== 'all' && !group.difficulties.includes(difficulty)) return false
      if (!normalized) return true
      return [group.term, ...group.occurrences.flatMap((entry) => [entry.meaningZh, entry.meaningInContextZh, entry.collocation])]
        .some((value) => value.toLocaleLowerCase().includes(normalized))
    })
  }, [difficulty, groups, query, savedOnly])
  const selected = groups.find((group) => group.normalizedTerm === selectedTerm)
  const currentReview = groups.find((group) => group.normalizedTerm === reviewTerms[reviewIndex])
  const reviewFinished = reviewTerms.length > 0 && reviewIndex >= reviewTerms.length
  const reviewableGroups = groups.filter((group) => !group.pending && (!weeklyReview || group.occurrences.some((entry) => {
    const issueDate = entry.itemReference?.issueDate
    return Boolean(issueDate && issueDate >= weeklyStart! && issueDate <= weeklyEnd!)
  })))

  const toggleExpanded = (term: string) => {
    const params = new URLSearchParams(searchParams)
    if (selectedTerm === term) params.delete('term')
    else params.set('term', term)
    setSearchParams(params)
  }
  const startReview = () => {
    const terms = [...reviewableGroups].filter((group) => !completedThisSession.has(group.normalizedTerm)).sort(reviewSort).slice(0, 5).map((group) => group.normalizedTerm)
    setReviewTerms(terms)
    setReviewIndex(0)
    setRevealed(false)
  }
  const finishWord = async () => {
    if (!currentReview || !revealed) return
    await markVocabularyReviewed(currentReview.normalizedTerm)
    setCompletedThisSession((current) => new Set(current).add(currentReview.normalizedTerm))
    setReviewIndex((value) => value + 1)
    setRevealed(false)
  }
  useEffect(() => {
    if (!loading && weeklyReview && reviewableGroups.length > 0 && !weeklyReviewStarted.current) {
      weeklyReviewStarted.current = true
      startReview()
    }
  }, [loading, reviewableGroups.length, weeklyReview])
  const retryPending = async (group: VocabularyGroup) => {
    setRetryingTerm(group.normalizedTerm)
    try {
      const result = await lookupGeneralDictionary(group.term)
      if (result.status !== 'resolved') {
        setToast(result.status === 'offline' ? '词典尚未缓存，请联网后再试' : '词典仍未收录这个词语')
        return
      }
      await resolvePendingVocabulary(group.normalizedTerm, result.vocabulary, {
        normalizedTerm: result.normalizedTerm,
        origin: result.origin,
        lookupStatus: 'resolved',
        dictionaryVersion: result.dictionaryVersion,
      })
      setToast('释义已补充，现在可以加入复习')
    } finally {
      setRetryingTerm(undefined)
    }
  }

  if (loading) return <section className="loading-state" aria-label="正在读取词汇"><span /><span /><span /></section>
  if (error) return <section className="empty-state page-enter"><p className="eyebrow">Language notebook</p><h1>暂时无法打开词汇本</h1><p>请重试；已经记录的词语和复习进度不会因此丢失。</p><button className="primary-button" type="button" onClick={() => void refresh()}>重试</button></section>

  return (
    <div className="local-page page-enter">
      <header className="local-hero local-hero--with-action">
        <div><p className="eyebrow">Language notebook</p><h1>词汇</h1><p>{weeklyReview ? `正在复习 ${weeklyStart} 至 ${weeklyEnd} 阅读时记录的词语。` : '回顾你在英文文章里点开过的词语，用每组 5 个词的翻卡练习慢慢积累表达。'}</p></div>
        <button type="button" className="primary-button" disabled={reviewableGroups.length === 0} onClick={startReview}>{weeklyReview ? '重新开始本周 5 词' : '开始 5 词复习'}</button>
      </header>

      {reviewTerms.length > 0 && <section className="review-panel" aria-live="polite">
        {reviewFinished ? <div className="review-complete"><p className="eyebrow">Review complete</p><h2>本组已完成</h2><p>刚刚复习了 {reviewTerms.length} 个词。你可以继续下一组，或回到词汇列表。</p><button type="button" className="primary-button" onClick={startReview}>开始下一组</button></div>
          : currentReview && <div className={`review-card${revealed ? ' is-revealed' : ''}`}>
            <div className="review-progress"><span>本组 {reviewIndex + 1}/{reviewTerms.length}</span><span>{currentReview.difficulties.join(' · ')} · {currentReview.occurrences.length} 个语境</span></div>
            <h2 lang="en">{currentReview.term}</h2>
            {!revealed ? <button type="button" className="primary-button" aria-expanded="false" onClick={() => setRevealed(true)}>揭示释义与语境</button>
              : <div className="review-answer" role="region" aria-label={`${currentReview.term} 的答案`}>
                <p><strong>{currentReview.occurrences[0].meaningZh}</strong></p>
                <p>{currentReview.occurrences[0].meaningInContextZh}</p>
                <p lang="en"><span>搭配：</span>{currentReview.occurrences[0].collocation}</p>
                <button type="button" className="primary-button" onClick={() => void finishWord()}>完成本词</button>
              </div>}
          </div>}
      </section>}

      <section className="vocabulary-controls" aria-label="词汇筛选">
        <label className="search-field"><span>搜索词汇</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="词语、中文释义或搭配" type="search" /></label>
        <div className="filter-row">
          <button type="button" className={!savedOnly ? 'is-active' : ''} onClick={() => setSavedOnly(false)}>全部</button>
          <button type="button" className={savedOnly ? 'is-active' : ''} onClick={() => setSavedOnly(true)}>已收藏</button>
        </div>
        <label className="select-field"><span>难度</span><select value={difficulty} onChange={(event) => setDifficulty(event.target.value as typeof difficulty)}><option value="all">全部难度</option>{difficultyOrder.map((value) => <option value={value} key={value}>{difficultyLabel(value)}</option>)}</select></label>
      </section>

      {filtered.length === 0 && <div className="inline-empty"><h2>{groups.length ? '没有匹配的词语' : '词库还是空的'}</h2><p>在英文文章中点击词语查看释义后，它会自动进入这里。</p></div>}
      <section className="vocabulary-grid" aria-label="词汇列表">{filtered.map((group) => {
        const isOpen = selected?.normalizedTerm === group.normalizedTerm
        return <article key={group.normalizedTerm} className={`vocabulary-card${isOpen ? ' is-open' : ''}${group.pending ? ' is-pending' : ''}`}>
          <button type="button" className="vocabulary-card__main" aria-expanded={isOpen} onClick={() => toggleExpanded(group.normalizedTerm)}>
            <span className="vocabulary-card__term" lang="en">{group.term}{group.pending && <small className="pending-badge">待补充</small>}</span>
            <span>{group.difficulties.map(difficultyLabel).join(' · ')} · {group.occurrences.length} 个语境</span>
            <small>首次 {formatDate(group.firstOpenedAt)} · 最近 {formatDate(group.lastOpenedAt)}</small>
            <small>{group.pending ? '补充释义后进入复习队列' : `已复习 ${group.reviewCount} 次 · ${group.lastReviewedAt ? `上次 ${formatDate(group.lastReviewedAt)}` : '尚未复习'}`}</small>
          </button>
          <button type="button" className={`vocabulary-card__save${group.saved ? ' is-active' : ''}`} aria-pressed={group.saved} aria-label={`${group.saved ? '取消收藏' : '收藏'} ${group.term}`} onClick={() => void setVocabularySaved(group.normalizedTerm, !group.saved)}>{group.saved ? '★' : '☆'}</button>
          {isOpen && <div className="vocabulary-contexts">
            {group.occurrences.map((entry) => <section key={entry.key}>
              <h3>{entry.meaningZh}</h3><p>{entry.meaningInContextZh}</p>
              {entry.contextExcerpt && <blockquote lang="en">{entry.contextExcerpt}</blockquote>}
              {entry.collocation && <p lang="en"><strong>搭配：</strong>{entry.collocation}</p>}
              {entry.itemReference ? <p className="vocabulary-source">来自：<a href={entry.itemReference.source.url} target="_blank" rel="noreferrer noopener">{entry.itemReference.title} · {entry.itemReference.source.name} ↗</a></p> : <p className="vocabulary-source">来源文章引用不可用</p>}
            </section>)}
            {group.pending && <button type="button" className="primary-button" disabled={retryingTerm === group.normalizedTerm} onClick={() => void retryPending(group)}>{retryingTerm === group.normalizedTerm ? '正在查词…' : '重新查词'}</button>}
          </div>}
        </article>
      })}</section>
      {toast && <div className="action-toast" role="status">{toast}</div>}
    </div>
  )
}
